import React, { useEffect, useState } from 'react';

import './AppLaunchLoader.css';

const LOADER_SESSION_KEY = 'qcl:app-launch-loader-seen';
const PREVIEW_QUERY_PARAM = 'loaderPreview';
const DEFAULT_VISIBLE_MS = 1450;
const REDUCED_MOTION_VISIBLE_MS = 500;
const EXIT_MS = 360;
const REDUCED_MOTION_EXIT_MS = 100;

type LoaderPhase = 'hidden' | 'visible' | 'exiting';

const shouldForcePreview = (): boolean => {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get(PREVIEW_QUERY_PARAM) === '1';
};

const hasSeenLoader = (): boolean => {
  if (typeof window === 'undefined') return true;
  if (shouldForcePreview()) return false;

  try {
    return window.sessionStorage.getItem(LOADER_SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

const markLoaderSeen = (): void => {
  if (typeof window === 'undefined' || shouldForcePreview()) return;

  try {
    window.sessionStorage.setItem(LOADER_SESSION_KEY, '1');
  } catch {
    // Session storage is a convenience guard only.
  }
};

function AppLaunchLoader(): React.ReactElement | null {
  const [phase, setPhase] = useState<LoaderPhase>('hidden');

  useEffect(() => {
    if (hasSeenLoader()) return;

    const reduceMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const visibleMs = reduceMotion ? REDUCED_MOTION_VISIBLE_MS : DEFAULT_VISIBLE_MS;
    const exitMs = reduceMotion ? REDUCED_MOTION_EXIT_MS : EXIT_MS;

    setPhase('visible');

    const exitTimer = window.setTimeout(() => {
      setPhase('exiting');
    }, visibleMs);
    const removeTimer = window.setTimeout(() => {
      markLoaderSeen();
      setPhase('hidden');
    }, visibleMs + exitMs);

    return () => {
      window.clearTimeout(exitTimer);
      window.clearTimeout(removeTimer);
    };
  }, []);

  if (phase === 'hidden') return null;

  return (
    <div
      className={`qcl-launch-loader qcl-launch-loader--${phase}`}
      aria-label="Loading QuantCheck Labs"
      aria-live="polite"
      role="status"
    >
      <div className="qcl-launch-loader__grid" aria-hidden="true" />
      <div className="qcl-launch-loader__content">
        <div className="qcl-launch-loader__brand" aria-label="QuantCheck Labs">
          <span className="qcl-launch-loader__mark">
            <img className="qcl-launch-loader__mark-image" src="/favicon.png" alt="" aria-hidden="true" />
          </span>
          <span className="qcl-launch-loader__wordmark">
            <span>QuantCheck</span>
            <span>Labs</span>
          </span>
        </div>

        <div className="qcl-launch-loader__terminal" aria-hidden="true">
          <div className="qcl-launch-loader__terminal-head">
            <span />
            <span />
            <span />
          </div>
          <div className="qcl-launch-loader__signal-row">
            <span>Stocks</span>
            <i />
            <span>Bonds</span>
            <i />
            <span>Commodities</span>
            <i />
            <span>Currencies</span>
            <i />
            <span>Crypto</span>
          </div>
          <div className="qcl-launch-loader__chart">
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
        </div>

        <div className="qcl-launch-loader__status">
          <span>Loading research workspace</span>
          <div className="qcl-launch-loader__progress" aria-hidden="true">
            <span />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppLaunchLoader;
